import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

/**
 * The console's claim, performed rather than stated.
 *
 * The white field does not vanish — it CONTRACTS into the searching chip and
 * then releases the answer. One surface throughout, so the reader watches a
 * single object change state rather than three things swapping places.
 *
 * The answer streams character by character the way a model answers, because
 * that is the behaviour being claimed: you asked, and it is already writing.
 */
const QUESTION = 'What changed on Northwind Labs this week?';
/**
 * The break is IN the string rather than set by measure, so it lands after
 * "keeps" at every width instead of wherever the line happens to run out —
 * and because the stream renders a growing slice of this string, the typed
 * output inherits the break for free. Rendered with `whitespace-pre-line`.
 */
const ANSWER = 'Answers come from memory Giga keeps\ncurrent on its own.';
const SCANNING = ['Slack', 'Notion', 'Linear', 'Drive', 'Gmail'];
type Phase = 'asking' | 'thinking' | 'answering' | 'holding';
export function AskLoop() {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState<Phase>('asking');
  const [typed, setTyped] = useState('');
  const [answer, setAnswer] = useState('');
  const [scan, setScan] = useState(0);
  useEffect(() => {
    if (reduce) return;
    let timer: number | undefined;
    // One effect owns the whole loop so two schedules can never run at once.
    if (phase === 'asking') {
      if (typed.length < QUESTION.length) {
        timer = window.setTimeout(() => setTyped(QUESTION.slice(0, typed.length + 1)), 32);
      } else {
        timer = window.setTimeout(() => {
          setScan(0);
          setPhase('thinking');
        }, 520);
      }
    } else if (phase === 'thinking') {
      // The sources cycle while it searches — the wait shows WHERE it is
      // looking rather than just that it is busy.
      if (scan < SCANNING.length - 1) {
        timer = window.setTimeout(() => setScan(s => s + 1), 260);
      } else {
        timer = window.setTimeout(() => {
          setAnswer('');
          setPhase('answering');
        }, 380);
      }
    } else if (phase === 'answering') {
      if (answer.length < ANSWER.length) {
        // Streams in small bursts rather than one glyph a frame, which is how
        // a model's output actually lands.
        timer = window.setTimeout(() => setAnswer(ANSWER.slice(0, answer.length + (Math.random() < 0.35 ? 2 : 1))), 26);
      } else {
        timer = window.setTimeout(() => setPhase('holding'), 3400);
      }
    } else {
      timer = window.setTimeout(() => {
        setTyped('');
        setAnswer('');
        setPhase('asking');
      }, 420);
    }
    return () => window.clearTimeout(timer);
  }, [phase, typed, answer, scan, reduce]);

  /*
   * THE AUTHORED LINE BREAK IS A DESKTOP DECISION.
   *
   * ANSWER carries a \n that balances the sentence at desktop width. Honoured
   * on a phone it produced three lines of wildly different lengths — the first
   * half wrapping on its own, then a two-word line, then the authored
   * remainder. Below `sm` the break is ignored and the sentence sets itself
   * inside a narrower measure, so every line is close to full.
   */
  const answerClass = 'font-title max-w-[22ch] whitespace-normal text-center text-[17px] font-bold leading-[1.22] tracking-tight text-[#FAF8F4] sm:max-w-[40ch] sm:whitespace-pre-line sm:text-2xl sm:leading-[1.2] md:text-[30px]';

  // Under reduced motion the loop never runs: the answer is simply stated.
  if (reduce) {
    return <div className="mb-10 flex justify-center md:mb-14" data-id="element-546" data-mp="7zgiqls7i7qn">
        <p className={answerClass} data-id="element-547" data-mp="7wubkn989u8y">
          {ANSWER}
        </p>
      </div>;
  }
  const streaming = phase === 'answering' || phase === 'holding';
  return <div className="mb-9 flex min-h-[132px] items-center justify-center md:mb-14 md:min-h-[144px]" data-id="element-548" data-mp="10mkjufo6i1x5">
      <AnimatePresence mode="popLayout" initial={false} data-id="element-549" data-mp="l4jkxive1uh3">
        {phase === 'asking' && <motion.div
      // The shared layoutId is what makes the field CONTRACT into the
      // searching chip instead of fading out and being replaced.
      key="field" layoutId="ask-surface" transition={{
        duration: 0.42,
        ease: [0.22, 1, 0.36, 1]
      }}
      /* ONE LINE HIGH ON A PHONE. Wrapped, the field became a tall lozenge
       with the send button floating in the space beside two ragged lines — a
       pill only reads as an input at one line. Smaller type and tighter
       padding keep the question on a single row at 360px. */ className="flex w-full max-w-[620px] items-center gap-2 rounded-full bg-white py-2 pl-4 pr-2 shadow-[0_0_0_7px_rgba(251,146,60,0.1),0_26px_56px_-18px_rgba(0,0,0,0.9)] sm:gap-3.5 sm:py-3 sm:pl-7 sm:pr-3 md:max-w-[720px]" data-id="element-550" data-mp="p76djlzvvjfc">
            <motion.p layout="position" className="min-w-0 flex-1 text-[12px] font-semibold leading-tight text-[#1A120C] sm:text-[17px] md:text-[18.5px]" data-id="element-551" data-mp="1rlohnxn0mvjq">
              {typed}
              <motion.span aria-hidden="true" className="ml-0.5 inline-block h-[1.05em] w-[2px] translate-y-[0.16em] bg-[#EA580C] sm:w-[2.5px]" animate={{
            opacity: [1, 0.15, 1]
          }} transition={{
            duration: 0.85,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-552" data-mp="37cz4am3b05jg" />
            </motion.p>
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#EA580C] text-white sm:size-11" data-id="element-553" data-mp="2tdr53t754xf9">
              <ArrowUp size={16} strokeWidth={2.4} className="sm:hidden" data-id="element-554" data-mp="2ofy0h4lhkbh3" />
              <ArrowUp size={19} strokeWidth={2.4} className="hidden sm:block" data-id="element-555" data-mp="32f7ebxm7ythw" />
            </span>
          </motion.div>}

        {phase === 'thinking' && <motion.div key="scanning" layoutId="ask-surface" transition={{
        duration: 0.42,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative flex items-center gap-2.5 overflow-hidden rounded-full bg-white/[0.08] py-2.5 pl-4 pr-5 ring-1 ring-inset ring-white/15" data-id="element-556" data-mp="hnf40l1lgmd3">
            {/* A sweep running across the chip — the frame is being read. */}
            <motion.span aria-hidden="true" className="pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-[#FB923C]/25 to-transparent" animate={{
          x: ['-140%', '340%']
        }} transition={{
          duration: 1.1,
          repeat: Infinity,
          ease: 'linear'
        }} data-id="element-557" data-mp="3bdk379hh2ij7" />
            <motion.span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-[#FB923C]" animate={{
          scale: [0.8, 1.25, 0.8],
          opacity: [0.5, 1, 0.5]
        }} transition={{
          duration: 0.8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-558" data-mp="2nnzj2n9l1f8c" />
            <span className="relative whitespace-nowrap text-[14px] font-medium text-white/70" data-id="element-559" data-mp="31j1yq248eiyc">
              Searching memory
            </span>
            {/* The source it is reading right now, swapping in place. */}
            <span className="relative flex h-[18px] w-[52px] items-center overflow-hidden" data-id="element-560" data-mp="21h566z0ahfau">
              <AnimatePresence mode="popLayout" initial={false} data-id="element-561" data-mp="1wik5kyh4ktlv">
                <motion.span key={SCANNING[scan]} initial={{
              opacity: 0,
              y: 9
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -9
            }} transition={{
              duration: 0.2,
              ease: [0.22, 1, 0.36, 1]
            }} className="absolute text-[13px] font-semibold text-[#FDBA74]" data-id="element-562" data-mp="1wj3lyar4n9xs">
                  {SCANNING[scan]}
                </motion.span>
              </AnimatePresence>
            </span>
          </motion.div>}

        {streaming && <motion.p key="answer" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        y: -8,
        filter: 'blur(5px)'
      }} transition={{
        duration: 0.26,
        ease: [0.22, 1, 0.36, 1]
      }} className={answerClass} data-id="element-563" data-mp="13uof4bi5fxxj">
            {answer}
            {/* A block caret while the stream is running, the way a model's
             output reads — it goes the moment the sentence is complete. */}
            {phase === 'answering' && <motion.span aria-hidden="true" className="ml-1 inline-block h-[0.72em] w-[0.42em] translate-y-[0.02em] rounded-[1px] bg-[#FB923C]" animate={{
          opacity: [1, 0.2, 1]
        }} transition={{
          duration: 0.7,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-564" data-mp="29nwxrj5cqni0" />}
          </motion.p>}
      </AnimatePresence>

      {/* Announced once, so a screen reader gets the claim without the theatre. */}
      <p className="sr-only" data-id="element-565" data-mp="1pyw81sqni9pl">{ANSWER}</p>
    </div>;
}